import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { restaurantScopedStorage } from '@/shared/store/restaurantScopedStorage'
import {
  DEFAULT_CONTENT_LANGUAGE,
  RESTAURANT_SETTINGS_SCHEMA_VERSION,
  WEEKDAY_COUNT
} from '../config'
import {
  OpeningHours,
  RestaurantSettings,
  RestaurantAddress,
  RestaurantContact,
  RestaurantIdentity
} from '../schema/settings'

/**
 * Where the data that is in the store came from. `owner` is anything saved in
 * Settings: from then on the document is the owner's, and no seed may replace
 * it.
 */
export type AddressSource =
  'none' | 'onboarding' | 'website-builder' | 'api' | 'owner'

/** What an owner's save leaves in `seededFrom`. */
const ownedFrom = (source: AddressSource): AddressSource =>
  source === 'none' ? 'owner' : source

const emptyAddress = (): RestaurantAddress => ({
  street: '',
  buildingNumber: '',
  postalCode: '',
  city: '',
  // The browser's own zone, which is right far more often than any default.
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
  unitNumber: '',
  deliveryNotes: ''
})

/**
 * Seven days, open by default, the same hours the website builder starts from
 * (`templates/settings.ts`). An owner corrects the two days that differ rather
 * than filling in all seven.
 */
const defaultHours = (): OpeningHours =>
  Array.from({ length: WEEKDAY_COUNT }, (_unused, weekday) => ({
    weekday,
    closed: false,
    opensAt: '12:00',
    closesAt: '22:00'
  }))

const defaultSettings = (): RestaurantSettings => ({
  identity: {
    name: '',
    legalName: '',
    nip: '',
    regon: '',
    legalForm: undefined,
    tagline: { [DEFAULT_CONTENT_LANGUAGE]: '' },
    description: { [DEFAULT_CONTENT_LANGUAGE]: '' }
  },
  address: emptyAddress(),
  contact: {
    phone: '',
    email: '',
    websiteUrl: '',
    facebookUrl: '',
    instagramUrl: ''
  },
  hours: defaultHours(),
  contentLanguage: DEFAULT_CONTENT_LANGUAGE
})

interface RestaurantSettingsState {
  /** The document. This, and only this, is what a future PUT would replace. */
  settings: RestaurantSettings

  /** So the seed runs once and can never overwrite a corrected address. */
  seededFrom: AddressSource

  /**
   * Which restaurant this data belongs to.
   *
   * Persisted and checked on read. The storage is already filed per venue
   * (`restaurantScopedStorage`), so this is the second line of defence: data
   * that reached the wrong venue's key anyway, such as a copy saved before the
   * scoping existed, is dropped rather than shown as the first venue's NIP under
   * the second venue's name.
   */
  restaurantId: string | null

  saveIdentity: (values: RestaurantIdentity) => void
  saveContact: (values: RestaurantContact) => void
  saveHours: (hours: OpeningHours) => void
  saveAddress: (values: RestaurantAddress) => void

  /**
   * The first address there has ever been, from the onboarding form, or lifted
   * out of a website document this browser already had.
   *
   * A no-op once `seededFrom !== 'none'`: a hydration that runs twice must never
   * replace an address the owner has since corrected.
   */
  seedAddress: (core: Partial<RestaurantAddress>, source: AddressSource) => void

  /** Whole-document replace, for when the endpoint starts hydrating this. */
  importSettings: (settings: RestaurantSettings) => void

  /** Drops everything, for a restaurant switch or a signed-out browser. */
  reset: (restaurantId: string | null) => void

  /** Files the data under `restaurantId`, keeping it. */
  claim: (restaurantId: string) => void
}

export const useRestaurantSettingsStore = create<RestaurantSettingsState>()(
  persist(
    (set, get) => ({
      settings: defaultSettings(),
      seededFrom: 'none',
      restaurantId: null,

      saveIdentity: values =>
        set(state => ({
          settings: { ...state.settings, identity: values },
          seededFrom: ownedFrom(state.seededFrom)
        })),

      saveContact: values =>
        set(state => ({
          settings: { ...state.settings, contact: values },
          seededFrom: ownedFrom(state.seededFrom)
        })),

      saveHours: hours =>
        set(state => ({
          settings: { ...state.settings, hours },
          seededFrom: ownedFrom(state.seededFrom)
        })),

      saveAddress: address =>
        set(state => ({
          settings: { ...state.settings, address },
          seededFrom: ownedFrom(state.seededFrom)
        })),

      seedAddress: (core, source) => {
        if (get().seededFrom !== 'none') return
        set(state => ({
          seededFrom: source,
          settings: {
            ...state.settings,
            address: { ...state.settings.address, ...core }
          }
        }))
      },

      importSettings: settings => set({ settings, seededFrom: 'api' }),

      reset: restaurantId =>
        set({
          settings: defaultSettings(),
          seededFrom: 'none',
          restaurantId
        }),

      claim: restaurantId => set({ restaurantId })
    }),
    {
      name: 'restaurant-settings-storage',
      storage: createJSONStorage(() => restaurantScopedStorage),
      version: RESTAURANT_SETTINGS_SCHEMA_VERSION,
      partialize: state => ({
        settings: state.settings,
        seededFrom: state.seededFrom,
        restaurantId: state.restaurantId
      }),
      /*
        Merged by hand, one level down, because zustand's own merge is shallow:
        a `settings` object persisted before a field existed would replace the
        defaults wholesale and the new field would arrive `undefined`. Here that
        means a blank NIP where the form expected a string, which throws in
        react-hook-form rather than showing an empty input. The loyalty and
        feedback stores carry the same note.
      */
      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as Partial<RestaurantSettingsState>
        const savedSettings = saved.settings ?? ({} as Partial<RestaurantSettings>)
        const base = defaultSettings()
        return {
          ...current,
          ...saved,
          settings: {
            identity: { ...base.identity, ...savedSettings.identity },
            address: { ...base.address, ...savedSettings.address },
            contact: { ...base.contact, ...savedSettings.contact },
            hours:
              savedSettings.hours?.length === WEEKDAY_COUNT
                ? savedSettings.hours
                : base.hours,
            contentLanguage:
              savedSettings.contentLanguage ?? base.contentLanguage
          }
        }
      }
    }
  )
)

export { defaultHours, defaultSettings, emptyAddress }
